import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { flowRight } from 'lodash';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import RowLabelTooltip from 'components/RowLabelTooltip/RowLabelTooltip';
import AlternateCurrencyChip
  from 'components/ScalarSpreadsheet/utilities/alternate-currency-chip/AlternateCurrencyChip';
import withAction from 'components/WithAction/withAction';
import withChart from 'components/WithChart/withChart';
import withChip from 'components/WithChip/withChip';
import withDialog from 'components/WithDialog/withDialog';
import withLink from 'components/WithLink/withLink';
import withRequired from 'components/WithRequired/withRequired';
import withTooltip from 'components/WithTooltip/withTooltip';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import { getCellViewerDisplayValue, getCellViewerFormat, getCellViewerSpanId } from './getCellViewerDisplayValue';

interface FastValueViewerProps {
  value?: any;
  cell: any;
  selected?: boolean;
  onDoubleClick?: (event: React.MouseEvent) => void;
  setDoubleClicked?: (value: boolean) => void;
}

const FastValueViewer: React.FC<FastValueViewerProps> = ({
  cell,
  value: notifiedValue,
  selected,
  onDoubleClick,
  setDoubleClicked,
}) => {
  const { tooltipRef, width } = useRevealEllipsisInTooltip();

  const keyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      const key = event.key || event.keyCode;
      if (selected && key && key.toString().toUpperCase() === 'F2') {
        //  We used this two functions and setters together because we wanted to adapt our
        //  code to work as the "new double click", a bunch of setters made by Jordan in ValueEditor
        //  to preserve the last value that was before the double click
        setDoubleClicked?.(true);
        onDoubleClick?.(event as unknown as React.MouseEvent);
      }
    },
    [onDoubleClick, selected, setDoubleClicked]
  );

  const { format } = useContext(FeaturedSpreadsheetContext);
  const cellFormat = useMemo(() => getCellViewerFormat(cell, format), [cell, format]);
  // Workbook events carry the raw value; the rendered cell can still be an older grid copy.
  const liveValue = notifiedValue === undefined ? cell.value : notifiedValue;
  const calcValue = useCallback(
    () => getCellViewerDisplayValue({ ...cell, value: liveValue }, cellFormat),
    [cell, cellFormat, liveValue]
  );
  const [value, setValue] = useState<any>();

  const getValue = useCallback(() => {
    setValue(calcValue());
  }, [calcValue]);

  useEffect(() => {
    if (!selected || cell.readOnly) {
      return undefined;
    }
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [cell.readOnly, keyUpHandler, selected]);

  // Reformat workbook notifications without waiting for cell selection to regenerate the grid.
  useEffect(() => {
    getValue();
  }, [cell.value, cell.currency, cell.suffix, cell.hidden, getValue]);

  const spanId = useMemo(() => getCellViewerSpanId(cell), [cell]);
  const valueOrNA = useMemo(() => (liveValue !== null ? value : 'N/A'), [liveValue, value]);

  if (
    cell.className?.includes('table-header') ||
    cell.className?.includes('header-no-border') ||
    cell.className?.includes('show-tooltip')
  ) {
    return (
      <span className="value-viewer" ref={tooltipRef}>
        {value ? (
          <RowLabelTooltip title={value} containerWidth={width}>
            <span id={`${cell.alias}-${cell.key}`} className="text-cell">
              {value}
            </span>
          </RowLabelTooltip>
        ) : (
          <span id={`${cell.alias}-${cell.key}`} className="text-cell">
            {value}
          </span>
        )}
      </span>
    );
  }

  if (cell.currencySpecification && format.currency.additionalCurrencies) {
    return (
      <AlternateCurrencyChip
        value={!cell.displayNAforNull ? value : valueOrNA}
        exchangeRate={cellFormat.currency.exchangeRate}
        spanId={spanId}
        className={cell.className}
        baseCurrency={format.currency.code as string}
        sourceCurrency={cellFormat.currency.sourceCurrency}
      />
    );
  }

  return (
    <span className="value-viewer">
      <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
        {!cell.displayNAforNull ? value : valueOrNA}
      </span>
    </span>
  );
};

// flowRight is a compose method alternative
const enhance = flowRight(withTooltip, withDialog, withChip, withRequired, withLink, withAction, withChart);

export default enhance(FastValueViewer);
